/**
 * FeedbackButtons - thumbs-up / thumbs-down for an AI recommendation.
 *
 * Replaces the decorative SentimentIcon (static lucide icons) with a live
 * feedback surface. On thumbs-down, a reason-picker sheet appears before the
 * API call so the cashier can tag *why* the insight missed.
 *
 * Behaviour contract:
 *  - Optimistic update: button state flips immediately, reverts on API error.
 *  - Idempotent: re-tapping the same thumb sends the upsert again (no-op on
 *    the server) and keeps UI consistent.
 *  - Tapping the other thumb overwrites the prior rating.
 *  - min-h-[44px] on each button satisfies the cashier-UX 44px tap floor.
 */

import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AiFeedbackRating, AiFeedbackReasonCode } from "../../lib/api";
import { submitAiFeedback } from "../../lib/api";
import { cn } from "../../lib/utils";

interface FeedbackButtonsProps {
  readonly recommendationId: string;
  readonly initial?: AiFeedbackRating | null | undefined;
}

const REASON_CODES: AiFeedbackReasonCode[] = ["inaccurate", "irrelevant", "too_generic"];

export function FeedbackButtons({ recommendationId, initial = null }: FeedbackButtonsProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState<AiFeedbackRating | null>(initial ?? null);
  const [showReasonPicker, setShowReasonPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(r: AiFeedbackRating, reasonCode?: AiFeedbackReasonCode, freeText?: string) {
    const prev = rating;
    setRating(r);
    setSubmitting(true);
    try {
      await submitAiFeedback({
        recommendation_id: recommendationId,
        rating: r,
        ...(reasonCode ? { reason_code: reasonCode } : {}),
        ...(freeText ? { free_text: freeText } : {}),
      });
    } catch {
      setRating(prev);
    } finally {
      setSubmitting(false);
    }
  }

  function handleThumbsUp() {
    void submit(1);
  }

  function handleThumbsDown() {
    setShowReasonPicker(true);
  }

  function handleReasonSubmit(reasonCode?: AiFeedbackReasonCode) {
    setShowReasonPicker(false);
    void submit(-1, reasonCode);
  }

  function handleReasonDismiss() {
    setShowReasonPicker(false);
  }

  if (showReasonPicker) {
    return (
      <ReasonPicker
        onSubmit={handleReasonSubmit}
        onDismiss={handleReasonDismiss}
        submitting={submitting}
        t={t}
      />
    );
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label={t("ai.feedback.thumbsUp")}
        aria-pressed={rating === 1}
        disabled={submitting}
        onClick={(e) => {
          e.stopPropagation();
          handleThumbsUp();
        }}
        className={cn(
          "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md transition-colors",
          "hover:bg-emerald-50 dark:hover:bg-emerald-950/30",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          rating === 1
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-muted hover:text-emerald-600 dark:hover:text-emerald-400",
        )}
      >
        <ThumbsUp
          className={cn("h-4 w-4", rating === 1 && "fill-emerald-600 dark:fill-emerald-400")}
          aria-hidden="true"
        />
      </button>
      <button
        type="button"
        aria-label={t("ai.feedback.thumbsDown")}
        aria-pressed={rating === -1}
        disabled={submitting}
        onClick={(e) => {
          e.stopPropagation();
          handleThumbsDown();
        }}
        className={cn(
          "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md transition-colors",
          "hover:bg-rose-50 dark:hover:bg-rose-950/30",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          rating === -1
            ? "text-rose-600 dark:text-rose-400"
            : "text-muted hover:text-rose-600 dark:hover:text-rose-400",
        )}
      >
        <ThumbsDown
          className={cn("h-4 w-4", rating === -1 && "fill-rose-600 dark:fill-rose-400")}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

// ---------- reason picker ----------

interface ReasonPickerProps {
  readonly onSubmit: (reasonCode?: AiFeedbackReasonCode) => void;
  readonly onDismiss: () => void;
  readonly submitting: boolean;
  readonly t: ReturnType<typeof useTranslation>["t"];
}

function ReasonPicker({ onSubmit, onDismiss, submitting, t }: ReasonPickerProps) {
  const [selected, setSelected] = useState<AiFeedbackReasonCode | null>(null);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-card p-3 shadow-elev-1 min-w-[200px]">
      <p className="text-xs font-semibold text-strong">{t("ai.feedback.whyNotHelpful")}</p>
      <div className="flex flex-col gap-1">
        {REASON_CODES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setSelected(selected === code ? null : code)}
            className={cn(
              "min-h-[44px] rounded-md px-3 text-left text-xs transition-colors",
              selected === code
                ? "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 font-medium"
                : "text-strong/80 hover:bg-surface-muted",
            )}
          >
            {t(`ai.feedback.reason.${code}`)}
          </button>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSubmit(selected ?? undefined)}
          disabled={submitting}
          className="flex-1 min-h-[44px] rounded-md bg-rose-600 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 transition-colors"
        >
          {t("ai.feedback.submit")}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-[44px] px-3 rounded-md text-xs text-muted hover:bg-surface-muted transition-colors"
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
